import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './codex.css';
import { codexApi } from './api';
import CodexNav from './components/CodexNav';
import OfflineIndicator from './components/OfflineIndicator';
import CodexOnboarding from './pages/CodexOnboarding';
import CodexDashboard from './pages/CodexDashboard';
import CodexDailyScroll from './pages/CodexDailyScroll';
import CodexBook from './pages/CodexBook';
import CodexJourney from './pages/CodexJourney';
import CodexJournal from './pages/CodexJournal';
import CodexLetters from './pages/CodexLetters';
import CodexContentManager from './pages/CodexContentManager';
import CodexChambersList from './pages/CodexChambersList';
import CodexChamberDetail from './pages/CodexChamberDetail';
import CodexAchievements from './pages/CodexAchievements';
import CodexTreasury from './pages/CodexTreasury';
import CodexMissions from './pages/CodexMissions';
import CodexChat from './pages/CodexChat';
import CodexAccessManager from './pages/CodexAccessManager';
import CodexIntro from './pages/CodexIntro';
import CodexMentor from './pages/CodexMentor';

export default function CodexApp() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null); // { progress, is_mentor, today_uk_date, title }
  const [showIntro, setShowIntro] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const data = await codexApi.me();
      setState(data);
      setLoading(false);
      return { ok: true, data };
    } catch (err) {
      setLoading(false);
      if (err.status === 401 || err.status === 403) {
        // Real auth failure — caller will redirect to /login
        setState(null);
        return { ok: false, authFailed: true };
      }
      if (err.status === 402) {
        // Logged in but NOT enrolled — surface the enroll-required screen instead of redirecting
        setState({ notEnrolled: true, message: err?.detail?.message || err.message, enrollUrl: err?.detail?.enroll_url || '/enroll' });
        return { ok: false, notEnrolled: true };
      }
      // Transient failure (network, 5xx) — DO NOT kick to login
      console.warn('CODEX /me transient error:', err);
      return { ok: false, authFailed: false, error: err };
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      // No token — go straight to login. The login page will return us here.
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.replace(`/login?next=${next}`);
      return;
    }
    refresh().then((result) => {
      if (result.authFailed) {
        // Only redirect on actual 401/403, not on transient errors
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/login?next=${next}`);
      }
    });
    // Decide whether to play intro:
    // - replay flag (?replay=1)
    // - first ever visit (no codex_intro_seen flag)
    // - on /codex root only (so direct deep links don't show it)
    const params = new URLSearchParams(window.location.search);
    const wantsReplay = params.get('replay') === '1';
    const seen = (() => { try { return localStorage.getItem('codex_intro_seen') === '1'; } catch { return false; } })();
    const onRoot = window.location.pathname === '/codex' || window.location.pathname === '/codex/';
    if (wantsReplay || (onRoot && !seen)) {
      setShowIntro(true);
    }
  }, []);

  if (loading) {
    return (
      <div className="codex-app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="codex-display" style={{ color: 'var(--codex-gold-light)', letterSpacing: '0.4em', fontSize: 14 }}>
          OPENING THE SCROLL…
        </div>
      </div>
    );
  }

  // Not logged in → already redirected to /login by useEffect above, render gracefully
  if (!state) {
    return (
      <div className="codex-app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <div className="codex-display" style={{ color: 'var(--codex-gold-light)', letterSpacing: '0.4em', fontSize: 14, marginBottom: 12 }}>
            OPENING THE SCROLL…
          </div>
          <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 18 }}>
            Hold on — connecting you to the Codex.
          </div>
          <button
            type="button"
            onClick={() => refresh()}
            className="codex-btn codex-btn-ghost"
            data-testid="codex-retry-load"
            style={{ padding: '8px 14px', fontSize: 12 }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Logged in but NOT enrolled (membership_tier !== 'mentorship') → show enroll-required gate.
  if (state?.notEnrolled) {
    return (
      <div className="codex-app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
        <div className="codex-card" style={{
          maxWidth: 520, width: '100%', padding: 36, textAlign: 'center',
          border: '2px solid rgba(212,175,55,0.4)',
          background: 'radial-gradient(120% 80% at 50% 0%, rgba(168,85,247,0.18) 0%, transparent 70%), linear-gradient(180deg, #1a0e2c, #0b0a16)',
        }} data-testid="codex-not-enrolled">
          <div style={{
            width: 96, height: 96, borderRadius: '50%', margin: '0 auto 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'radial-gradient(circle at 50% 35%, rgba(212,175,55,0.6), rgba(168,85,247,0.2) 60%, transparent 75%)',
            border: '2px solid var(--codex-gold)',
          }}>
            <span style={{ fontSize: 40 }}>🔐</span>
          </div>
          <div className="codex-eyebrow" style={{ color: 'var(--codex-gold)', marginBottom: 8 }}>SACRED INNER COURT</div>
          <h1 className="codex-display" style={{ fontSize: 28, color: 'var(--codex-gold-light)', marginBottom: 12 }}>
            The Codex is for the Enrolled
          </h1>
          <p style={{ color: 'rgba(253,246,227,0.85)', marginBottom: 22, lineHeight: 1.6 }}>
            {state.message || 'The CODEX is reserved for mentees who have committed to the Mentorship journey. Enrol to step inside the scrolls.'}
          </p>
          <a
            href={state.enrollUrl || '/enroll'}
            className="codex-btn codex-btn-primary"
            data-testid="codex-not-enrolled-cta"
            style={{ padding: '12px 22px', fontSize: 14, textDecoration: 'none', display: 'inline-flex' }}
          >
            Begin My Mentorship →
          </a>
          <div style={{ marginTop: 14 }}>
            <a
              href="/dashboard"
              style={{ fontSize: 12, color: 'rgba(245,217,124,0.7)', textDecoration: 'underline' }}
              data-testid="codex-not-enrolled-back"
            >
              Back to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  const { progress, is_mentor } = state;

  // Logged in but not onboarded → force onboarding.
  if (!progress?.onboarded) {
    return (
      <div className="codex-app">
        <CodexOnboarding onComplete={refresh} />
      </div>
    );
  }

  // Cinematic intro — overlay
  if (showIntro) {
    return (
      <div className="codex-app">
        <CodexIntro onDone={() => {
          setShowIntro(false);
          // Strip ?replay=1 if present, route to dashboard
          if (location.pathname === '/codex' || location.pathname === '/codex/') {
            navigate('/codex/dashboard', { replace: true });
          } else {
            // remove ?replay=1
            const params = new URLSearchParams(location.search);
            params.delete('replay');
            navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
          }
        }} />
      </div>
    );
  }

  return (
    <div className="codex-app">
      <CodexNav user={null} isMentor={is_mentor} progress={progress} />
      <main style={{ padding: '14px', maxWidth: 1280, margin: '0 auto' }}>
        <Routes>
          <Route path="/" element={<Navigate to="/codex/dashboard" replace />} />
          <Route path="dashboard" element={<CodexDashboard state={state} refresh={refresh} />} />
          <Route path="scroll" element={<CodexDailyScroll refresh={refresh} />} />
          <Route path="codex-book" element={<CodexBook />} />
          <Route path="chambers" element={<CodexChambersList />} />
          <Route path="chambers/:chamberId" element={<CodexChamberDetail />} />
          <Route path="journey" element={<CodexJourney state={state} />} />
          <Route path="journal" element={<CodexJournal />} />
          <Route path="letters" element={<CodexLetters />} />
          <Route path="achievements" element={<CodexAchievements />} />
          <Route path="treasury" element={<CodexTreasury />} />
          <Route path="missions" element={<CodexMissions />} />
          <Route path="chat" element={<CodexChat />} />
          <Route path="chat/:peerId" element={<CodexChat />} />
          <Route path="access" element={<CodexAccessManager />} />
          {is_mentor && <Route path="content" element={<CodexContentManager />} />}
          {is_mentor && <Route path="mentor" element={<CodexMentor />} />}
          {is_mentor && <Route path="mentor/:userId" element={<CodexMentor />} />}
          <Route path="*" element={<Navigate to="/codex/dashboard" replace />} />
        </Routes>
      </main>
      <OfflineIndicator />
    </div>
  );
}
